import { createClient } from 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2.111.0/+esm';

const roots = document.querySelectorAll('[data-staffing-crm]:not([data-crm-initialized])');
roots.forEach(initCRM);

function initCRM(root) {
  root.dataset.crmInitialized = 'true';

  const supabaseUrl = root.dataset.supabaseUrl?.trim();
  const supabaseKey = root.dataset.supabaseKey?.trim();
  if (!supabaseUrl || !supabaseKey) {
    root.innerHTML = '<div class="crm-auth"><div class="crm-auth-card"><h2>Setup required</h2><p>Add the Supabase project URL and publishable key in this section\'s Shopify theme settings.</p></div></div>';
    return;
  }

  const sb = createClient(supabaseUrl, supabaseKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
  });

  const state = {
    user: null,
    profile: null,
    membership: null,
    orgId: null,
    data: { candidates: [], clients: [], contacts: [], jobs: [], submissions: [], interviews: [], offers: [], placements: [], tasks: [], activities: [] },
    metrics: null,
    detail: null,
    team: []
  };

  const $ = (s) => root.querySelector(s);
  const $$ = (s) => [...root.querySelectorAll(s)];
  const authScreen = $('[data-auth-screen]');
  const app = $('[data-app]');
  const modal = $('[data-modal]');
  const modalTitle = $('[data-modal-title]');
  const modalBody = $('[data-modal-body]');
  const toast = $('[data-toast]');
  const detailBackdrop = $('[data-detail-backdrop]');
  const detailBody = $('[data-detail-body]');
  const detailActions = $('[data-detail-actions]');

  function escapeHTML(value='') {
    return String(value).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]));
  }

  function fmtDate(v) {
    if (!v) return '-';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
  }

  function fmtMoney(v) {
    if (v === null || v === undefined || v === '') return '-';
    return new Intl.NumberFormat(undefined,{style:'currency',currency:'USD',maximumFractionDigits:0}).format(Number(v));
  }

  function showToast(message, error=false) {
    toast.textContent = message;
    toast.classList.toggle('is-error', error);
    toast.hidden = false;
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => { toast.hidden = true; }, 3200);
  }

  function showAuthMessage(message, error=false) {
    const el = $('[data-auth-message]');
    el.textContent = message || '';
    el.classList.toggle('is-error', error);
  }

  async function logActivity(activity_type, subject, body='', links={}) {
    if (!state.orgId) return;
    await sb.from('activities').insert({
      organization_id: state.orgId,
      activity_type,
      subject,
      body,
      ...links
    });
  }

  // -------------------- AUTH --------------------

  $$('[data-auth-tab]').forEach(btn => btn.addEventListener('click', () => {
    $$('[data-auth-tab]').forEach(x => x.classList.toggle('is-active', x === btn));
    $('[data-signin-form]').hidden = btn.dataset.authTab !== 'signin';
    $('[data-signup-form]').hidden = btn.dataset.authTab !== 'signup';
    showAuthMessage('');
  }));

  $('[data-signin-form]').addEventListener('submit', async (e) => {
    e.preventDefault();
    showAuthMessage('Signing in...');
    const fd = new FormData(e.currentTarget);
    const { error } = await sb.auth.signInWithPassword({
      email: fd.get('email'),
      password: fd.get('password')
    });
    if (error) return showAuthMessage(error.message, true);
    await boot();
  });

  $('[data-signup-form]').addEventListener('submit', async (e) => {
    e.preventDefault();
    showAuthMessage('Creating your account...');
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get('email')).trim();
    const password = String(fd.get('password'));
    const fullName = String(fd.get('full_name')).trim();
    const companyName = String(fd.get('company_name')).trim();

    const { data, error } = await sb.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, company_name: companyName } }
    });

    if (error) return showAuthMessage(error.message, true);
    if (!data.session) {
      showAuthMessage('Account created. Check your email to confirm your address, then sign in.');
      return;
    }

    try {
      await bootstrapCompany(fullName, companyName);
      await boot();
    } catch (err) {
      showAuthMessage(err.message || 'Could not create company.', true);
    }
  });

  $('[data-signout]').addEventListener('click', async () => {
    await sb.auth.signOut();
    state.user = null;
    app.hidden = true;
    authScreen.hidden = false;
  });

  async function bootstrapCompany(fullName, companyName) {
    const { data: userData, error: userError } = await sb.auth.getUser();
    if (userError || !userData.user) throw userError || new Error('No authenticated user.');
    const userId = userData.user.id;

    const { data: existing } = await sb.from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();
    if (existing) return existing.organization_id;

    const slug = companyName.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,48) + '-' + crypto.randomUUID().slice(0,6);

    const { data: org, error: orgError } = await sb.from('organizations')
      .insert({ name: companyName, slug, created_by: userId })
      .select('id')
      .single();
    if (orgError) throw orgError;

    const { error: memberError } = await sb.from('organization_members').insert({
      organization_id: org.id,
      user_id: userId,
      role: 'owner',
      status: 'active'
    });
    if (memberError) throw memberError;

    const { error: profileError } = await sb.from('user_profiles').upsert({
      user_id: userId,
      full_name: fullName
    });
    if (profileError) throw profileError;

    return org.id;
  }

  async function boot() {
    showAuthMessage('Loading workspace...');
    const { data: userData, error: userError } = await sb.auth.getUser();
    if (userError || !userData.user) {
      authScreen.hidden = false;
      app.hidden = true;
      return;
    }
    state.user = userData.user;

    try {
      await sb.rpc('activate_my_staffing_memberships');
    } catch (_) {
      // v2 installs can still reach this UI before migration 003 is applied.
    }

    const [{ data: profile }, { data: membership, error: memError }] = await Promise.all([
      sb.from('user_profiles').select('*').eq('user_id', state.user.id).maybeSingle(),
      sb.from('organization_members').select('organization_id, role, status').eq('user_id', state.user.id).eq('status','active').maybeSingle()
    ]);

    if (memError) {
      showAuthMessage(memError.message, true);
      return;
    }
    let activeMembership = membership;
    if (!activeMembership) {
      const pendingName = state.user.user_metadata?.full_name || '';
      const pendingCompany = state.user.user_metadata?.company_name || '';
      if (pendingCompany) {
        try {
          await bootstrapCompany(pendingName || state.user.email || 'Owner', pendingCompany);
          const { data: bootstrapped, error: bootstrapLookupError } = await sb.from('organization_members')
            .select('organization_id, role, status')
            .eq('user_id', state.user.id)
            .eq('status','active')
            .maybeSingle();
          if (bootstrapLookupError) throw bootstrapLookupError;
          activeMembership = bootstrapped;
        } catch (err) {
          console.error(err);
        }
      }
    }

    if (!activeMembership) {
      authScreen.hidden = false;
      app.hidden = true;
      showAuthMessage('Your account is not connected to an active staffing organization.', true);
      return;
    }

    state.profile = profile;
    state.membership = activeMembership;
    state.orgId = activeMembership.organization_id;

    $('[data-user-email]').textContent = state.user.email || '';
    $('[data-first-name]').textContent = (profile?.full_name || state.user.email || 'Recruiter').split(/[\s@]/)[0];
    authScreen.hidden = true;
    app.hidden = false;
    await refreshAll();
  }

  // -------------------- NAVIGATION --------------------

  $$('[data-view-button]').forEach(btn => btn.addEventListener('click', () => {
    activateView(btn.dataset.viewButton);
    $('.crm-sidebar').classList.remove('is-open');
  }));

  function activateView(name) {
    $$('[data-view-button]').forEach(b => b.classList.toggle('is-active', b.dataset.viewButton === name));
    $$('[data-view]').forEach(v => v.classList.toggle('is-active', v.dataset.view === name));
  }

  $('[data-mobile-menu]').addEventListener('click', () => $('.crm-sidebar').classList.toggle('is-open'));

  // -------------------- DATA --------------------

  async function refreshAll() {
    if (!state.orgId) return;

    const [
      candidatesRes, clientsRes, contactsRes, jobsRes, submissionsRes, interviewsRes, offersRes, placementsRes, tasksRes, activitiesRes, metricsRes
    ] = await Promise.all([
      sb.from('candidates').select('*').eq('organization_id', state.orgId).order('updated_at',{ascending:false}),
      sb.from('clients').select('*').eq('organization_id', state.orgId).order('name'),
      sb.from('contacts').select('*, clients(name)').eq('organization_id', state.orgId).order('last_name'),
      sb.from('jobs').select('*, clients(name)').eq('organization_id', state.orgId).order('updated_at',{ascending:false}),
      sb.from('submissions').select('*, candidates(first_name,last_name), jobs(title, client_id, clients(name))').eq('organization_id', state.orgId).order('updated_at',{ascending:false}),
      sb.from('interviews').select('*, submissions(candidate_id, job_id, candidates(first_name,last_name), jobs(title, clients(name)))').eq('organization_id', state.orgId).order('starts_at',{ascending:true}),
      sb.from('offers').select('*, candidates(first_name,last_name), jobs(title), clients(name)').eq('organization_id', state.orgId).order('created_at',{ascending:false}),
      sb.from('placements').select('*, candidates(first_name,last_name), jobs(title), clients(name)').eq('organization_id', state.orgId).order('created_at',{ascending:false}),
      sb.from('tasks').select('*').eq('organization_id', state.orgId).order('due_at',{ascending:true,nullsFirst:false}),
      sb.from('activities').select('*').eq('organization_id', state.orgId).order('occurred_at',{ascending:false}).limit(20),
      sb.rpc('staffing_dashboard_metrics',{target_org:state.orgId})
    ]);

    const errors = [candidatesRes,clientsRes,contactsRes,jobsRes,submissionsRes,interviewsRes,offersRes,placementsRes,tasksRes,activitiesRes,metricsRes].map(x=>x.error).filter(Boolean);
    if (errors.length) {
      console.error(errors);
      showToast(errors[0].message, true);
      return;
    }

    state.data.candidates = candidatesRes.data || [];
    state.data.clients = clientsRes.data || [];
    state.data.contacts = contactsRes.data || [];
    state.data.jobs = jobsRes.data || [];
    state.data.submissions = submissionsRes.data || [];
    state.data.interviews = interviewsRes.data || [];
    state.data.offers = offersRes.data || [];
    state.data.placements = placementsRes.data || [];
    state.data.tasks = tasksRes.data || [];
    state.data.activities = activitiesRes.data || [];
    state.metrics = metricsRes.data || null;

    renderAll();
  }

  function renderAll() {
    renderCandidates();
    renderClients();
    renderContacts();
    renderJobs();
    renderSubmissions();
    renderInterviews();
    renderOffers();
    renderPlacements();
    renderTasks();
    renderDashboard();
    bindRecordOpeners();
    if (state.membership && ['owner','admin'].includes(state.membership.role)) loadTeam();
  }

  function renderCandidates() {
    const q = ($('[data-filter="candidates"]')?.value || '').toLowerCase();
    const st = $('[data-status-filter="candidates"]')?.value || '';
    const rows = state.data.candidates.filter(c => {
      const hay = [c.first_name,c.last_name,c.current_title,c.city,c.state,...(c.skills||[])].join(' ').toLowerCase();
      return (!q || hay.includes(q)) && (!st || c.status === st);
    });
    $('[data-table="candidates"]').innerHTML = rows.length ? rows.map(c => `
      <tr class="crm-clickable-row" data-open-candidate="${c.id}">
        <td><strong>${escapeHTML(c.first_name)} ${escapeHTML(c.last_name)}</strong><br><small>${escapeHTML(c.email||'')}</small></td>
        <td>${escapeHTML(c.current_title||'-')}</td>
        <td>${escapeHTML([c.city,c.state].filter(Boolean).join(', ')||'-')}</td>
        <td><span class="crm-pill">${escapeHTML(c.status)}</span></td>
        <td>${(c.skills||[]).slice(0,3).map(s=>`<span class="crm-tag">${escapeHTML(s)}</span>`).join('') || '-'}</td>
        <td>${fmtDate(c.last_contact_at)}</td>
      </tr>`).join('') : emptyRow(6,'No candidates found.');
  }

  function renderClients() {
    const q = ($('[data-filter="clients"]')?.value || '').toLowerCase();
    const rows = state.data.clients.filter(c => [c.name,c.industry,c.city,c.state].join(' ').toLowerCase().includes(q));
    $('[data-table="clients"]').innerHTML = rows.length ? rows.map(c => `
      <tr class="crm-clickable-row" data-open-client="${c.id}">
        <td><strong>${escapeHTML(c.name)}</strong><br><small>${escapeHTML(c.website||'')}</small></td>
        <td>${escapeHTML(c.industry||'-')}</td>
        <td>${escapeHTML([c.city,c.state].filter(Boolean).join(', ')||'-')}</td>
        <td><span class="crm-pill">${escapeHTML(c.status)}</span></td>
        <td>${escapeHTML(c.phone||'-')}</td>
      </tr>`).join('') : emptyRow(5,'No clients found.');
  }


  function renderContacts() {
    const q = ($('[data-filter="contacts"]')?.value || '').toLowerCase();
    const rows = state.data.contacts.filter(c => [c.first_name,c.last_name,c.title,c.email,c.clients?.name].join(' ').toLowerCase().includes(q));
    $('[data-table="contacts"]').innerHTML = rows.length ? rows.map(c => `
      <tr>
        <td><strong>${escapeHTML(c.first_name)} ${escapeHTML(c.last_name)}</strong>${c.is_decision_maker ? '<br><span class="crm-pill">Decision Maker</span>' : ''}</td>
        <td>${escapeHTML(c.clients?.name||'-')}</td>
        <td>${escapeHTML(c.title||'-')}</td>
        <td>${escapeHTML(c.email||'-')}</td>
        <td>${escapeHTML(c.phone||'-')}</td>
      </tr>`).join('') : emptyRow(5,'No contacts found.');
  }

  function renderJobs() {
    const q = ($('[data-filter="jobs"]')?.value || '').toLowerCase();
    const st = $('[data-status-filter="jobs"]')?.value || '';
    const rows = state.data.jobs.filter(j => {
      const hay = [j.title,j.clients?.name,j.city,j.state,j.employment_type].join(' ').toLowerCase();
      return (!q || hay.includes(q)) && (!st || j.status === st);
    });
    $('[data-table="jobs"]').innerHTML = rows.length ? rows.map(j => `
      <tr class="crm-clickable-row" data-open-job="${j.id}">
        <td><strong>${escapeHTML(j.title)}</strong></td>
        <td>${escapeHTML(j.clients?.name||'-')}</td>
        <td>${escapeHTML([j.city,j.state].filter(Boolean).join(', ')||'-')}</td>
        <td>${escapeHTML(j.employment_type||'-')}</td>
        <td>${j.openings ?? 1}</td>
        <td><span class="crm-pill">${escapeHTML(j.status)}</span></td>
      </tr>`).join('') : emptyRow(6,'No jobs found.');
  }

  function renderSubmissions() {
    const rows = state.data.submissions;
    $('[data-table="submissions"]').innerHTML = rows.length ? rows.map(s => `
      <tr>
        <td><strong>${escapeHTML(s.candidates?.first_name||'')} ${escapeHTML(s.candidates?.last_name||'')}</strong></td>
        <td>${escapeHTML(s.jobs?.title||'-')}</td>
        <td>${escapeHTML(s.jobs?.clients?.name||'-')}</td>
        <td><span class="crm-pill">${escapeHTML(s.status)}</span></td>
        <td>${fmtDate(s.submitted_at || s.created_at)}</td>
      </tr>`).join('') : emptyRow(5,'No submissions yet.');
  }


  function renderInterviews() {
    const rows = state.data.interviews;
    $('[data-table="interviews"]').innerHTML = rows.length ? rows.map(i => `
      <tr>
        <td><strong>${escapeHTML(i.submissions?.candidates?.first_name||'')} ${escapeHTML(i.submissions?.candidates?.last_name||'')}</strong></td>
        <td>${escapeHTML(i.submissions?.jobs?.title||'-')}</td>
        <td>${escapeHTML(i.submissions?.jobs?.clients?.name||'-')}</td>
        <td>${i.starts_at ? new Date(i.starts_at).toLocaleString() : '-'}</td>
        <td>${escapeHTML(i.interview_type||'-')}</td>
        <td><span class="crm-pill">${escapeHTML(i.status)}</span></td>
      </tr>`).join('') : emptyRow(6,'No interviews scheduled.');
  }

  function renderOffers() {
    const rows = state.data.offers;
    $('[data-table="offers"]').innerHTML = rows.length ? rows.map(o => `
      <tr>
        <td><strong>${escapeHTML(o.candidates?.first_name||'')} ${escapeHTML(o.candidates?.last_name||'')}</strong></td>
        <td>${escapeHTML(o.jobs?.title||'-')}</td>
        <td>${escapeHTML(o.clients?.name||'-')}</td>
        <td>${o.salary ? fmtMoney(o.salary) : o.hourly_rate ? fmtMoney(o.hourly_rate) + '/hr' : '-'}</td>
        <td>${fmtDate(o.start_date)}</td>
        <td><span class="crm-pill">${escapeHTML(o.status)}</span></td>
      </tr>`).join('') : emptyRow(6,'No offers yet.');
  }

  function renderPlacements() {
    const rows = state.data.placements;
    $('[data-table="placements"]').innerHTML = rows.length ? rows.map(p => `
      <tr>
        <td><strong>${escapeHTML(p.candidates?.first_name||'')} ${escapeHTML(p.candidates?.last_name||'')}</strong></td>
        <td>${escapeHTML(p.jobs?.title||'-')}</td>
        <td>${escapeHTML(p.clients?.name||'-')}</td>
        <td>${fmtDate(p.start_date)}</td>
        <td>${fmtDate(p.end_date)}</td>
        <td><span class="crm-pill">${escapeHTML(p.status)}</span></td>
        <td>${fmtMoney(p.estimated_revenue)}</td>
      </tr>`).join('') : emptyRow(7,'No placements yet.');
  }

  function renderTasks() {
    const rows = state.data.tasks;
    $('[data-table="tasks"]').innerHTML = rows.length ? rows.map(t => `
      <tr>
        <td><strong>${escapeHTML(t.title)}</strong><br><small>${escapeHTML(t.description||'')}</small></td>
        <td>${fmtDate(t.due_at)}</td>
        <td>${escapeHTML(t.priority)}</td>
        <td><span class="crm-pill">${escapeHTML(t.status)}</span></td>
        <td>${t.status !== 'completed' ? `<button class="crm-link-button" data-complete-task="${t.id}">Complete</button>` : ''}</td>
      </tr>`).join('') : emptyRow(5,'No tasks.');
    $$('[data-complete-task]').forEach(b => b.addEventListener('click', () => completeTask(b.dataset.completeTask)));
  }


  function bindRecordOpeners() {
    $$('[data-open-candidate]').forEach(el => el.onclick = () => openCandidateDetail(el.dataset.openCandidate));
    $$('[data-open-client]').forEach(el => el.onclick = () => openClientDetail(el.dataset.openClient));
    $$('[data-open-job]').forEach(el => el.onclick = () => openJobDetail(el.dataset.openJob));
  }

  async function loadTeam() {
    if (!state.orgId || !['owner','admin'].includes(state.membership?.role)) {
      const row = $('[data-table="team"]');
      if (row) row.innerHTML = emptyRow(5,'Owner or admin access required.');
      return;
    }
    const { data, error } = await sb.rpc('staffing_team_directory',{target_org:state.orgId});
    if (error) {
      const row = $('[data-table="team"]');
      if (row) row.innerHTML = emptyRow(5,error.message);
      return;
    }
    state.team = data || [];
    const row = $('[data-table="team"]');
    if (!row) return;
    row.innerHTML = state.team.length ? state.team.map(m=>`
      <tr>
        <td><strong>${escapeHTML(m.full_name||'User')}</strong></td>
        <td>${escapeHTML(m.email||'')}</td>
        <td><span class="crm-pill">${escapeHTML(m.role)}</span></td>
        <td>${escapeHTML(m.status)}</td>
        <td>${fmtDate(m.joined_at)}</td>
      </tr>`).join('') : emptyRow(5,'No team members.');
  }

  function renderDashboard() {
    $('[data-kpi="open_jobs"]').textContent = state.metrics?.open_jobs ?? state.data.jobs.filter(x=>x.status==='open').length;
    $('[data-kpi="candidates"]').textContent = state.metrics?.active_candidates ?? state.data.candidates.filter(x=>!['archived','unavailable'].includes(x.status)).length;
    $('[data-kpi="submissions"]').textContent = state.metrics?.submissions_30d ?? state.data.submissions.length;
    $('[data-kpi="placements"]').textContent = state.metrics?.active_placements ?? state.data.placements.filter(x=>['active','upcoming'].includes(x.status)).length;

    const openTasks = state.data.tasks.filter(t=>t.status!=='completed').slice(0,6);
    $('[data-dashboard-tasks]').innerHTML = openTasks.length ? openTasks.map(t => `
      <div class="crm-task-row"><strong>${escapeHTML(t.title)}</strong><small>${fmtDate(t.due_at)} - ${escapeHTML(t.priority)}</small></div>
    `).join('') : '<div class="crm-empty">No open tasks.</div>';

    const endingSoon = state.data.placements.filter(p => {
      if (!p.end_date || p.status === 'completed') return false;
      const days = (new Date(p.end_date) - new Date()) / 86400000;
      return days >= 0 && days <= 30;
    }).slice(0,4);

    const staleCandidates = state.data.candidates.filter(c => {
      if (!c.last_contact_at || ['placed','archived'].includes(c.status)) return false;
      return (new Date() - new Date(c.last_contact_at)) / 86400000 > 30;
    }).slice(0,4);

    const actions = [
      ...endingSoon.map(p=>({title:`Redeploy ${p.candidates?.first_name||'candidate'} ${p.candidates?.last_name||''}`,meta:`Assignment ends ${fmtDate(p.end_date)}.`})),
      ...staleCandidates.map(c=>({title:`Contact ${c.first_name} ${c.last_name}`,meta:`No recorded contact in 30+ days.`}))
    ].slice(0,6);

    $('[data-next-actions]').innerHTML = actions.length ? actions.map(a=>`
      <div class="crm-card-row"><strong>${escapeHTML(a.title)}</strong><small>${escapeHTML(a.meta)}</small></div>
    `).join('') : '<div class="crm-empty">No urgent actions detected.</div>';

    $('[data-activity-feed]').innerHTML = state.data.activities.length ? state.data.activities.map(a=>`
      <div class="crm-activity-row"><strong>${escapeHTML(a.subject||a.activity_type)}</strong><br><small>${escapeHTML(a.body||'')} ${fmtDate(a.occurred_at)}</small></div>
    `).join('') : '<div class="crm-empty">Activity will appear as your team works.</div>';
  }

  function emptyRow(cols,text){ return `<tr><td colspan="${cols}" class="crm-empty">${escapeHTML(text)}</td></tr>`; }

  $$('[data-filter]').forEach(el => el.addEventListener('input', renderAll));
  $$('[data-status-filter]').forEach(el => el.addEventListener('change', renderAll));


  // -------------------- RECORD DETAIL WORKSPACE --------------------

  $('[data-close-detail]').addEventListener('click', closeDetail);
  detailBackdrop.addEventListener('click', e => { if (e.target === detailBackdrop) closeDetail(); });

  function closeDetail() {
    detailBackdrop.hidden = true;
    detailBody.innerHTML = '';
    detailActions.innerHTML = '';
    state.detail = null;
  }

  function openDetailShell(title, subtitle='') {
    detailBackdrop.hidden = false;
    detailBody.innerHTML = `
      <div class="crm-detail-loading">
        <p class="crm-eyebrow">${escapeHTML(subtitle)}</p>
        <h2>${escapeHTML(title)}</h2>
        <div class="crm-empty">Loading record...</div>
      </div>`;
  }

  function detailSection(title, html) {
    return `<section class="crm-detail-section"><h3>${escapeHTML(title)}</h3>${html}</section>`;
  }

  function timelineHTML(items=[]) {
    if (!items.length) return '<div class="crm-empty">No activity recorded.</div>';
    return `<div class="crm-timeline">${items.map(a=>`
      <div class="crm-timeline-item">
        <span class="crm-timeline-dot"></span>
        <div><strong>${escapeHTML(a.subject||a.activity_type||'Activity')}</strong>
        <p>${escapeHTML(a.body||'')}</p><small>${a.occurred_at ? new Date(a.occurred_at).toLocaleString() : ''}</small></div>
      </div>`).join('')}</div>`;
  }

  async function openCandidateDetail(id) {
    openDetailShell('Candidate','ATS PROFILE');
    const { data, error } = await sb.rpc('staffing_candidate_detail',{target_candidate:id});
    if (error || !data?.candidate) {
      detailBody.innerHTML = `<div class="crm-empty">${escapeHTML(error?.message||'Candidate not found.')}</div>`;
      return;
    }
    state.detail = {type:'candidate',id,data};
    const c = data.candidate;
    detailActions.innerHTML = `<button class="crm-primary" data-detail-add-note>Add Note</button>`;
    detailBody.innerHTML = `
      <div class="crm-detail-hero">
        <div>
          <p class="crm-eyebrow">CANDIDATE</p>
          <h2>${escapeHTML(c.first_name)} ${escapeHTML(c.last_name)}</h2>
          <p>${escapeHTML(c.current_title||'Candidate')} ${c.current_employer ? ' at '+escapeHTML(c.current_employer) : ''}</p>
        </div>
        <div class="crm-detail-status">
          <label>Status
            <select data-candidate-status>
              ${['new','screening','qualified','available','submitted','interviewing','offer','placed','unavailable','archived'].map(s=>`<option value="${s}" ${c.status===s?'selected':''}>${s}</option>`).join('')}
            </select>
          </label>
        </div>
      </div>
      <div class="crm-detail-kpis">
        <article><span>Location</span><strong>${escapeHTML([c.city,c.state].filter(Boolean).join(', ')||'--')}</strong></article>
        <article><span>Availability</span><strong>${escapeHTML(c.availability||'unknown')}</strong></article>
        <article><span>Experience</span><strong>${c.years_experience ?? '--'} yrs</strong></article>
        <article><span>Submissions</span><strong>${data.submissions.length}</strong></article>
      </div>
      <div class="crm-detail-grid">
        <div>
          ${detailSection('Contact',`<div class="crm-info-grid"><div><span>Email</span><strong>${escapeHTML(c.email||'--')}</strong></div><div><span>Phone</span><strong>${escapeHTML(c.phone||'--')}</strong></div><div><span>Desired Hourly</span><strong>${fmtMoney(c.desired_hourly_rate)}</strong></div><div><span>Desired Salary</span><strong>${fmtMoney(c.desired_salary)}</strong></div></div>`)}
          ${detailSection('Skills', data.skills.length ? `<div class="crm-tags">${data.skills.map(s=>`<span class="crm-tag">${escapeHTML(s.name)}</span>`).join('')}</div>` : '<div class="crm-empty">No normalized skills yet.</div>')}
          ${detailSection('Certifications', data.certifications.length ? `<div class="crm-list">${data.certifications.map(x=>`<div class="crm-list-row"><strong>${escapeHTML(x.name)}</strong><span>${x.expires_on ? 'Expires '+fmtDate(x.expires_on) : escapeHTML(x.verification_status||'')}</span></div>`).join('')}</div>` : '<div class="crm-empty">No certifications.</div>')}
          ${detailSection('Documents', `<div data-candidate-docs>${data.documents.length ? data.documents.map(d=>`<button class="crm-file-row" data-open-doc="${d.storage_path}">${escapeHTML(d.file_name)}</button>`).join('') : '<div class="crm-empty">No documents.</div>'}</div><label class="crm-upload-inline">Upload document<input type="file" data-detail-upload accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"></label>`)}
        </div>
        <div>
          ${detailSection('Submissions', data.submissions.length ? `<div class="crm-list">${data.submissions.map(s=>`<div class="crm-list-row"><div><strong>${escapeHTML(s.job_title)}</strong><small>${escapeHTML(s.client_name)}</small></div><span class="crm-pill">${escapeHTML(s.status)}</span></div>`).join('')}</div>` : '<div class="crm-empty">No submissions.</div>')}
          ${detailSection('Interviews & Offers', `<div class="crm-list">${[
            ...data.interviews.map(i=>`<div class="crm-list-row"><div><strong>Interview - ${escapeHTML(i.job_title)}</strong><small>${fmtDate(i.starts_at)} - ${escapeHTML(i.client_name)}</small></div><span class="crm-pill">${escapeHTML(i.status)}</span></div>`),
            ...data.offers.map(o=>`<div class="crm-list-row"><div><strong>Offer - ${escapeHTML(o.job_title)}</strong><small>${escapeHTML(o.client_name)}</small></div><span class="crm-pill">${escapeHTML(o.status)}</span></div>`)
          ].join('') || '<div class="crm-empty">No interviews or offers.</div>'}</div>`)}
          ${detailSection('Placements', data.placements.length ? `<div class="crm-list">${data.placements.map(p=>`<div class="crm-list-row"><div><strong>${escapeHTML(p.job_title)}</strong><small>${escapeHTML(p.client_name)} - ${fmtDate(p.start_date)}</small></div><span class="crm-pill">${escapeHTML(p.status)}</span></div>`).join('')}</div>` : '<div class="crm-empty">No placements.</div>')}
        </div>
      </div>
      ${detailSection('Timeline',timelineHTML(data.activities))}
    `;
    $('[data-candidate-status]').onchange = async e => {
      const { error } = await sb.from('candidates').update({status:e.target.value}).eq('id',id).eq('organization_id',state.orgId);
      if (error) return showToast(error.message,true);
      await logActivity('candidate_status_changed',`Candidate status changed to ${e.target.value}`,'',{candidate_id:id});
      showToast('Candidate updated.');
      await refreshAll();
    };
    $('[data-detail-add-note]').onclick = () => openNoteModal('candidate',id);
    $$('[data-open-doc]').forEach(b => b.onclick = async () => {
      const { data:signed, error } = await sb.storage.from('staffing-documents').createSignedUrl(b.dataset.openDoc,60);
      if (error) return showToast(error.message,true);
      window.open(signed.signedUrl,'_blank','noopener');
    });
    const upload = $('[data-detail-upload]');
    if (upload) upload.onchange = async () => {
      const f=upload.files?.[0]; if(!f) return;
      try { await uploadCandidateDocument(id,f); showToast('Document uploaded.'); await openCandidateDetail(id); }
      catch(err){ showToast(err.message||'Upload failed.',true); }
    };
  }

  async function openClientDetail(id) {
    openDetailShell('Client','ACCOUNT');
    const { data, error } = await sb.rpc('staffing_client_detail',{target_client:id});
    if (error || !data?.client) {
      detailBody.innerHTML = `<div class="crm-empty">${escapeHTML(error?.message||'Client not found.')}</div>`;
      return;
    }
    state.detail={type:'client',id,data};
    const c=data.client;
    detailActions.innerHTML=`<button class="crm-primary" data-detail-add-note>Add Note</button>`;
    detailBody.innerHTML=`
      <div class="crm-detail-hero"><div><p class="crm-eyebrow">CLIENT ACCOUNT</p><h2>${escapeHTML(c.name)}</h2><p>${escapeHTML(c.industry||'')}</p></div>
      <div class="crm-detail-status"><label>Status<select data-client-status>${['prospect','active','inactive'].map(s=>`<option value="${s}" ${c.status===s?'selected':''}>${s}</option>`).join('')}</select></label></div></div>
      <div class="crm-detail-kpis">
        <article><span>Open Jobs</span><strong>${data.jobs.filter(j=>j.status==='open').length}</strong></article>
        <article><span>Contacts</span><strong>${data.contacts.length}</strong></article>
        <article><span>Placements</span><strong>${data.placements.length}</strong></article>
        <article><span>Est. Revenue</span><strong>${fmtMoney(data.estimated_revenue)}</strong></article>
      </div>
      <div class="crm-detail-grid">
        <div>
          ${detailSection('Account',`<div class="crm-info-grid"><div><span>Phone</span><strong>${escapeHTML(c.phone||'--')}</strong></div><div><span>Website</span><strong>${escapeHTML(c.website||'--')}</strong></div><div><span>Location</span><strong>${escapeHTML([c.city,c.state].filter(Boolean).join(', ')||'--')}</strong></div><div><span>Payment Terms</span><strong>${escapeHTML(c.payment_terms||'--')}</strong></div></div>`)}
          ${detailSection('Contacts',data.contacts.length?`<div class="crm-list">${data.contacts.map(x=>`<div class="crm-list-row"><div><strong>${escapeHTML(x.first_name)} ${escapeHTML(x.last_name)}</strong><small>${escapeHTML(x.title||'')} ${x.is_decision_maker?' - Decision Maker':''}</small></div><span>${escapeHTML(x.email||'')}</span></div>`).join('')}</div>`:'<div class="crm-empty">No contacts.</div>')}
        </div>
        <div>
          ${detailSection('Jobs',data.jobs.length?`<div class="crm-list">${data.jobs.map(j=>`<button class="crm-list-row crm-list-button" data-detail-open-job="${j.id}"><div><strong>${escapeHTML(j.title)}</strong><small>${escapeHTML([j.city,j.state].filter(Boolean).join(', '))}</small></div><span class="crm-pill">${escapeHTML(j.status)}</span></button>`).join('')}</div>`:'<div class="crm-empty">No jobs.</div>')}
          ${detailSection('Placements',data.placements.length?`<div class="crm-list">${data.placements.map(p=>`<div class="crm-list-row"><div><strong>${escapeHTML(p.candidate_name)}</strong><small>${escapeHTML(p.job_title)}</small></div><span>${fmtMoney(p.estimated_revenue)}</span></div>`).join('')}</div>`:'<div class="crm-empty">No placements.</div>')}
        </div>
      </div>
      ${detailSection('Timeline',timelineHTML(data.activities))}
    `;
    $('[data-client-status]').onchange=async e=>{
      const {error}=await sb.from('clients').update({status:e.target.value}).eq('id',id).eq('organization_id',state.orgId);
      if(error)return showToast(error.message,true);
      await logActivity('client_status_changed',`Client status changed to ${e.target.value}`,'',{client_id:id});
      showToast('Client updated.'); await refreshAll();
    };
    $('[data-detail-add-note]').onclick=()=>openNoteModal('client',id);
    $$('[data-detail-open-job]').forEach(b=>b.onclick=()=>openJobDetail(b.dataset.detailOpenJob));
  }

  const pipelineStages=['identified','contacted','interested','qualified','submitted','client_review','interview','offer','placed','rejected'];

  async function openJobDetail(id) {
    openDetailShell('Job','JOB ORDER');
    const [{data,error},{data:matches,error:matchError}] = await Promise.all([
      sb.rpc('staffing_job_detail',{target_job:id}),
      sb.rpc('match_candidates_for_job',{target_job:id,result_limit:20})
    ]);
    if(error || !data?.job){
      detailBody.innerHTML=`<div class="crm-empty">${escapeHTML(error?.message||'Job not found.')}</div>`; return;
    }
    state.detail={type:'job',id,data};
    const j=data.job;
    detailActions.innerHTML=`<button class="crm-primary" data-detail-add-note>Add Note</button>`;
    const byStage=Object.fromEntries(pipelineStages.map(s=>[s,data.submissions.filter(x=>x.status===s)]));
    const existing=new Set(data.submissions.map(x=>x.candidate_id));
    detailBody.innerHTML=`
      <div class="crm-detail-hero"><div><p class="crm-eyebrow">JOB ORDER</p><h2>${escapeHTML(j.title)}</h2><p>${escapeHTML(j.client_name||'')} - ${escapeHTML([j.city,j.state].filter(Boolean).join(', '))}</p></div>
      <div class="crm-detail-status"><label>Status<select data-job-status>${['open','on_hold','filled','closed'].map(s=>`<option value="${s}" ${j.status===s?'selected':''}>${s}</option>`).join('')}</select></label></div></div>
      <div class="crm-detail-kpis">
        <article><span>Openings</span><strong>${j.openings||1}</strong></article>
        <article><span>Pipeline</span><strong>${data.submissions.length}</strong></article>
        <article><span>Interviews</span><strong>${data.interviews.length}</strong></article>
        <article><span>Offers</span><strong>${data.offers.length}</strong></article>
      </div>
      ${detailSection('Requirements',`<div class="crm-info-grid"><div><span>Type</span><strong>${escapeHTML(j.employment_type)}</strong></div><div><span>Pay</span><strong>${j.pay_min||j.pay_max ? `${fmtMoney(j.pay_min)} - ${fmtMoney(j.pay_max)}` : '--'}</strong></div><div><span>Bill Rate</span><strong>${fmtMoney(j.bill_rate)}</strong></div><div><span>Start</span><strong>${fmtDate(j.start_date)}</strong></div></div><div class="crm-tags">${(j.required_skills||[]).map(s=>`<span class="crm-tag">${escapeHTML(s)}</span>`).join('')}</div>`)}
      ${detailSection('Candidate Pipeline',`<div class="crm-pipeline-board">${pipelineStages.slice(0,9).map(stage=>`
        <div class="crm-pipeline-column"><div class="crm-pipeline-title">${escapeHTML(stage)} <span>${byStage[stage].length}</span></div>
          <div>${byStage[stage].map(s=>`<div class="crm-pipeline-card" data-pipeline-candidate="${s.candidate_id}"><strong>${escapeHTML(s.candidate_name)}</strong><small>${escapeHTML(s.candidate_title||'')}</small><select data-move-submission="${s.id}">${pipelineStages.map(x=>`<option value="${x}" ${x===s.status?'selected':''}>${x}</option>`).join('')}</select></div>`).join('')||'<div class="crm-pipeline-empty">Empty</div>'}</div>
        </div>`).join('')}</div>`)}
      ${detailSection('Recommended Candidates',matchError?`<div class="crm-empty">${escapeHTML(matchError.message)}</div>`:(matches||[]).length?`<div class="crm-match-list">${matches.map(m=>`
        <div class="crm-match-row">
          <div class="crm-match-score">${m.match_score}%</div>
          <div><strong>${escapeHTML(m.candidate_name)}</strong><small>${escapeHTML(m.current_title||'')} - ${escapeHTML(m.location||'')}</small><div class="crm-tags">${(m.matched_skills||[]).slice(0,4).map(s=>`<span class="crm-tag">${escapeHTML(s)}</span>`).join('')}</div></div>
          <button class="${existing.has(m.candidate_id)?'crm-secondary':'crm-primary'}" data-add-match="${m.candidate_id}" ${existing.has(m.candidate_id)?'disabled':''}>${existing.has(m.candidate_id)?'In Pipeline':'Add to Pipeline'}</button>
        </div>`).join('')}</div>`:'<div class="crm-empty">No matches found.</div>')}
      ${detailSection('Timeline',timelineHTML(data.activities))}
    `;
    $('[data-job-status]').onchange=async e=>{
      const {error}=await sb.from('jobs').update({status:e.target.value}).eq('id',id).eq('organization_id',state.orgId);
      if(error)return showToast(error.message,true);
      await logActivity('job_status_changed',`Job status changed to ${e.target.value}`,'',{job_id:id,client_id:j.client_id});
      showToast('Job updated.'); await refreshAll();
    };
    $('[data-detail-add-note]').onclick=()=>openNoteModal('job',id);
    $$('[data-pipeline-candidate]').forEach(card=>card.onclick=e=>{ if(e.target.tagName!=='SELECT') openCandidateDetail(card.dataset.pipelineCandidate); });
    $$('[data-move-submission]').forEach(sel=>sel.onchange=async()=>{
      const {error}=await sb.from('submissions').update({status:sel.value,submitted_at:['submitted','client_review','interview','offer','placed'].includes(sel.value)?new Date().toISOString():null}).eq('id',sel.dataset.moveSubmission).eq('organization_id',state.orgId);
      if(error)return showToast(error.message,true);
      await logActivity('submission_stage_changed',`Candidate moved to ${sel.value}`,'',{job_id:id,client_id:j.client_id});
      showToast('Pipeline updated.'); await openJobDetail(id); await refreshAll();
    });
    $$('[data-add-match]').forEach(btn=>btn.onclick=async()=>{
      const payload={organization_id:state.orgId,job_id:id,candidate_id:btn.dataset.addMatch,recruiter_id:state.user.id,status:'identified'};
      const {error}=await sb.from('submissions').insert(payload);
      if(error)return showToast(error.message,true);
      await logActivity('candidate_matched','Candidate added to job pipeline','',{job_id:id,candidate_id:btn.dataset.addMatch,client_id:j.client_id});
      showToast('Candidate added to pipeline.'); await openJobDetail(id); await refreshAll();
    });
  }

  function openNoteModal(type,id) {
    modalTitle.textContent='Add Note';
    modalBody.innerHTML=`<form class="crm-form" data-note-form><label>Note<textarea name="note" required autofocus></textarea></label><button class="crm-primary">Save Note</button></form>`;
    modal.hidden=false;
    modalBody.querySelector('[data-note-form]').onsubmit=async e=>{
      e.preventDefault();
      const note=new FormData(e.currentTarget).get('note');
      const links={};
      if(type==='candidate')links.candidate_id=id;
      if(type==='client')links.client_id=id;
      if(type==='job')links.job_id=id;
      const {error}=await sb.from('activities').insert({organization_id:state.orgId,activity_type:'note',subject:'Note added',body:note,...links});
      if(error)return showToast(error.message,true);
      closeModal(); showToast('Note saved.');
      if(type==='candidate') await openCandidateDetail(id);
      if(type==='client') await openClientDetail(id);
      if(type==='job') await openJobDetail(id);
      await refreshAll();
    };
  }


  // -------------------- GLOBAL SEARCH --------------------

  let searchTimer;
  $('[data-global-search]').addEventListener('input', (e) => {
    clearTimeout(searchTimer);
    const q = e.target.value.trim().toLowerCase();
    if (q.length < 2) { $('[data-search-results]').hidden = true; return; }
    searchTimer = setTimeout(() => {
      const items = [
        ...state.data.candidates.filter(x=>[x.first_name,x.last_name,x.current_title,x.email].join(' ').toLowerCase().includes(q)).slice(0,5).map(x=>({type:'Candidate',id:x.id,title:`${x.first_name} ${x.last_name}`,sub:x.current_title||x.email||''})),
        ...state.data.clients.filter(x=>[x.name,x.industry].join(' ').toLowerCase().includes(q)).slice(0,5).map(x=>({type:'Client',id:x.id,title:x.name,sub:x.industry||''})),
        ...state.data.jobs.filter(x=>[x.title,x.clients?.name].join(' ').toLowerCase().includes(q)).slice(0,5).map(x=>({type:'Job',id:x.id,title:x.title,sub:x.clients?.name||''}))
      ].slice(0,10);
      const box = $('[data-search-results]');
      box.innerHTML = items.length ? items.map(i=>`<button class="crm-search-item crm-search-button" data-search-open="${i.type}:${i.id}"><strong>${escapeHTML(i.title)}</strong><span>${escapeHTML(i.type)} - ${escapeHTML(i.sub)}</span></button>`).join('') : '<div class="crm-search-item">No matches.</div>';
      box.hidden = false;
      box.querySelectorAll('[data-search-open]').forEach(b=>b.onclick=()=>{
        const [type,id]=b.dataset.searchOpen.split(':');
        box.hidden=true;
        if(type==='Candidate')openCandidateDetail(id);
        if(type==='Client')openClientDetail(id);
        if(type==='Job')openJobDetail(id);
      });
    }, 120);
  });

  // -------------------- CREATE MODALS --------------------

  $('[data-open-create]').addEventListener('click', () => openChooser());
  $$('[data-create-type]').forEach(b => b.addEventListener('click', () => openCreate(b.dataset.createType)));
  $('[data-close-modal]').addEventListener('click', closeModal);
  modal.addEventListener('click', e => { if (e.target === modal) closeModal(); });

  $('[data-invite-member]').addEventListener('click', () => {
    if (!['owner','admin'].includes(state.membership?.role)) return showToast('Owner or admin access required.',true);
    modalTitle.textContent='Invite Team Member';
    modalBody.innerHTML=`<form class="crm-form" data-invite-form>
      <label>Email<input name="email" type="email" required></label>
      <label>Role<select name="role">
        <option value="recruiter">Recruiter</option>
        <option value="account_manager">Account Manager</option>
        <option value="sales_manager">Sales Manager</option>
        <option value="compliance">Compliance</option>
        <option value="finance">Finance</option>
        <option value="read_only">Read Only</option>
        ${state.membership.role==='owner'?'<option value="admin">Admin</option>':''}
      </select></label>
      <button class="crm-primary">Send Invitation</button>
    </form>`;
    modal.hidden=false;
    modalBody.querySelector('[data-invite-form]').onsubmit=async e=>{
      e.preventDefault();
      const fd=new FormData(e.currentTarget);
      const {data,error}=await sb.functions.invoke('invite-staff-member',{body:{organization_id:state.orgId,email:fd.get('email'),role:fd.get('role')}});
      if(error)return showToast(error.message||'Invitation failed.',true);
      if(data?.ok===false)return showToast(data.error||'Invitation failed.',true);
      closeModal(); showToast('Invitation sent.'); await loadTeam();
    };
  });


  function openChooser() {
    modalTitle.textContent = 'Add Record';
    modalBody.innerHTML = `
      <div class="crm-form-grid">
        <button class="crm-primary" data-choose="candidate">Candidate</button>
        <button class="crm-primary" data-choose="client">Client</button>
        <button class="crm-primary" data-choose="contact">Contact</button>
        <button class="crm-primary" data-choose="job">Job</button>
        <button class="crm-primary" data-choose="submission">Submission</button>
        <button class="crm-primary" data-choose="interview">Interview</button>
        <button class="crm-primary" data-choose="offer">Offer</button>
        <button class="crm-primary" data-choose="placement">Placement</button>
        <button class="crm-primary" data-choose="task">Task</button>
      </div>`;
    modal.hidden = false;
    modalBody.querySelectorAll('[data-choose]').forEach(b=>b.addEventListener('click',()=>openCreate(b.dataset.choose)));
  }

  function openCreate(type) {
    const builders = {
      candidate: candidateForm,
      client: clientForm,
      contact: contactForm,
      job: jobForm,
      submission: submissionForm,
      interview: interviewForm,
      offer: offerForm,
      placement: placementForm,
      task: taskForm
    };
    if (!builders[type]) return;
    modalTitle.textContent = `Add ${type[0].toUpperCase()+type.slice(1)}`;
    modalBody.innerHTML = builders[type]();
    modal.hidden = false;
    const form = modalBody.querySelector('form');
    form.addEventListener('submit', e => submitCreate(type,e));
  }

  function closeModal(){ modal.hidden = true; modalBody.innerHTML = ''; }

  function options(items,labelFn,valueFn=x=>x.id) {
    return items.map(x=>`<option value="${escapeHTML(valueFn(x))}">${escapeHTML(labelFn(x))}</option>`).join('');
  }

  function candidateForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label>First Name<input name="first_name" required></label>
        <label>Last Name<input name="last_name" required></label>
        <label>Email<input name="email" type="email"></label>
        <label>Phone<input name="phone"></label>
        <label>Current Title<input name="current_title"></label>
        <label>Current Employer<input name="current_employer"></label>
        <label>City<input name="city"></label>
        <label>State<input name="state"></label>
        <label>Status<select name="status"><option>new</option><option>screening</option><option>qualified</option><option>available</option><option>submitted</option><option>interviewing</option><option>placed</option><option>unavailable</option></select></label>
        <label>Availability<select name="availability"><option>unknown</option><option>available_now</option><option>2_weeks</option><option>30_days</option><option>not_available</option></select></label>
        <label class="span-2">Skills, comma separated<input name="skills" placeholder="OSHA Compliance, Safety Training, Construction"></label>
        <label class="span-2">Certifications, comma separated<input name="certifications" placeholder="OSHA 500, CHST"></label>
        <label class="span-2">Resume / Document<input name="resume" type="file" accept=".pdf,.doc,.docx"></label>
        <label class="span-2">Summary<textarea name="summary"></textarea></label>
      </div>
      <button class="crm-primary">Save Candidate</button>
    </form>`; }

  function clientForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Company Name<input name="name" required></label>
        <label>Industry<input name="industry"></label>
        <label>Status<select name="status"><option>prospect</option><option>active</option><option>inactive</option></select></label>
        <label>Phone<input name="phone"></label>
        <label>Website<input name="website"></label>
        <label>City<input name="city"></label>
        <label>State<input name="state"></label>
        <label>Placement Fee %<input name="placement_fee_pct" type="number" step=".01"></label>
        <label>Contract Markup %<input name="contract_markup_pct" type="number" step=".01"></label>
        <label class="span-2">Notes<textarea name="notes"></textarea></label>
      </div>
      <button class="crm-primary">Save Client</button>
    </form>`; }


  function contactForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Client<select name="client_id" required><option value="">Select client</option>${options(state.data.clients,x=>x.name)}</select></label>
        <label>First Name<input name="first_name" required></label>
        <label>Last Name<input name="last_name" required></label>
        <label>Title<input name="title"></label>
        <label>Email<input name="email" type="email"></label>
        <label>Phone<input name="phone"></label>
        <label>Decision Maker<select name="is_decision_maker"><option value="false">No</option><option value="true">Yes</option></select></label>
        <label class="span-2">Notes<textarea name="notes"></textarea></label>
      </div>
      <button class="crm-primary">Save Contact</button>
    </form>`; }

  function jobForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Client<select name="client_id" required><option value="">Select client</option>${options(state.data.clients,x=>x.name)}</select></label>
        <label class="span-2">Job Title<input name="title" required></label>
        <label>City<input name="city"></label>
        <label>State<input name="state"></label>
        <label>Employment Type<select name="employment_type"><option>contract</option><option>permanent</option><option>contract_to_hire</option><option>temporary</option></select></label>
        <label>Openings<input name="openings" type="number" min="1" value="1"></label>
        <label>Pay Min<input name="pay_min" type="number" step=".01"></label>
        <label>Pay Max<input name="pay_max" type="number" step=".01"></label>
        <label>Bill Rate<input name="bill_rate" type="number" step=".01"></label>
        <label>Start Date<input name="start_date" type="date"></label>
        <label class="span-2">Required Skills<input name="required_skills" placeholder="comma separated"></label>
        <label class="span-2">Required Certifications<input name="required_certifications" placeholder="comma separated"></label>
        <label class="span-2">Description<textarea name="description"></textarea></label>
      </div>
      <button class="crm-primary">Save Job</button>
    </form>`; }

  function submissionForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Candidate<select name="candidate_id" required><option value="">Select candidate</option>${options(state.data.candidates,x=>`${x.first_name} ${x.last_name}`)}</select></label>
        <label class="span-2">Job<select name="job_id" required><option value="">Select job</option>${options(state.data.jobs.filter(x=>x.status==='open'),x=>`${x.title} - ${x.clients?.name||''}`)}</select></label>
        <label>Status<select name="status"><option>identified</option><option>contacted</option><option>interested</option><option>qualified</option><option>submitted</option><option>client_review</option><option>interview</option><option>offer</option><option>placed</option><option>rejected</option></select></label>
        <label>Candidate Rate<input name="candidate_rate" type="number" step=".01"></label>
        <label>Client Bill Rate<input name="client_bill_rate" type="number" step=".01"></label>
        <label class="span-2">Recruiter Notes<textarea name="recruiter_notes"></textarea></label>
      </div>
      <button class="crm-primary">Save Submission</button>
    </form>`; }


  function interviewForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Submission<select name="submission_id" required><option value="">Select submission</option>${options(state.data.submissions,x=>`${x.candidates?.first_name||''} ${x.candidates?.last_name||''} - ${x.jobs?.title||''}`)}</select></label>
        <label>Interview Type<select name="interview_type"><option>video</option><option>phone</option><option>in_person</option></select></label>
        <label>Status<select name="status"><option>scheduled</option><option>completed</option><option>cancelled</option><option>no_show</option></select></label>
        <label class="span-2">Starts At<input name="starts_at" type="datetime-local" required></label>
        <label class="span-2">Location / Meeting Link<input name="location_or_link"></label>
        <label class="span-2">Interviewers<input name="interviewer_names"></label>
      </div>
      <button class="crm-primary">Save Interview</button>
    </form>`; }

  function offerForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Submission<select name="submission_id" required><option value="">Select submission</option>${options(state.data.submissions,x=>`${x.candidates?.first_name||''} ${x.candidates?.last_name||''} - ${x.jobs?.title||''}`)}</select></label>
        <label>Offer Type<select name="offer_type"><option>permanent</option><option>contract</option><option>contract_to_hire</option><option>temporary</option></select></label>
        <label>Status<select name="status"><option>draft</option><option>sent</option><option>negotiating</option><option>accepted</option><option>declined</option></select></label>
        <label>Salary<input name="salary" type="number" step=".01"></label>
        <label>Hourly Rate<input name="hourly_rate" type="number" step=".01"></label>
        <label>Bill Rate<input name="bill_rate" type="number" step=".01"></label>
        <label>Bonus<input name="bonus" type="number" step=".01"></label>
        <label>Start Date<input name="start_date" type="date"></label>
        <label class="span-2">Notes<textarea name="notes"></textarea></label>
      </div>
      <button class="crm-primary">Save Offer</button>
    </form>`; }

  function placementForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Candidate<select name="candidate_id" required><option value="">Select candidate</option>${options(state.data.candidates,x=>`${x.first_name} ${x.last_name}`)}</select></label>
        <label class="span-2">Job<select name="job_id" required><option value="">Select job</option>${options(state.data.jobs,x=>`${x.title} - ${x.clients?.name||''}`)}</select></label>
        <label>Type<select name="placement_type"><option>contract</option><option>permanent</option><option>contract_to_hire</option><option>temporary</option></select></label>
        <label>Status<select name="status"><option>upcoming</option><option>active</option><option>completed</option><option>cancelled</option></select></label>
        <label>Start Date<input name="start_date" type="date" required></label>
        <label>End Date<input name="end_date" type="date"></label>
        <label>Pay Rate<input name="pay_rate" type="number" step=".01"></label>
        <label>Bill Rate<input name="bill_rate" type="number" step=".01"></label>
        <label>Annual Salary<input name="annual_salary" type="number" step=".01"></label>
        <label>Placement Fee %<input name="placement_fee_pct" type="number" step=".01"></label>
        <label>Estimated Revenue<input name="estimated_revenue" type="number" step=".01"></label>
      </div>
      <button class="crm-primary">Save Placement</button>
    </form>`; }

  function taskForm(){ return `
    <form class="crm-form" data-create-form>
      <div class="crm-form-grid">
        <label class="span-2">Task<input name="title" required></label>
        <label>Due<input name="due_at" type="datetime-local"></label>
        <label>Priority<select name="priority"><option>low</option><option selected>normal</option><option>high</option><option>urgent</option></select></label>
        <label class="span-2">Description<textarea name="description"></textarea></label>
      </div>
      <button class="crm-primary">Save Task</button>
    </form>`; }

  function cleanNumber(v) { return v === '' || v === null ? null : Number(v); }
  function cleanArray(v) { return String(v||'').split(',').map(x=>x.trim()).filter(Boolean); }

  async function submitCreate(type,e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const btn = form.querySelector('button[type="submit"], button.crm-primary');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }

    try {
      if (type === 'candidate') {
        const payload = {
          organization_id: state.orgId,
          first_name: fd.get('first_name'),
          last_name: fd.get('last_name'),
          email: fd.get('email') || null,
          phone: fd.get('phone') || null,
          current_title: fd.get('current_title') || null,
          current_employer: fd.get('current_employer') || null,
          city: fd.get('city') || null,
          state: fd.get('state') || null,
          status: fd.get('status'),
          availability: fd.get('availability'),
          skills: cleanArray(fd.get('skills')),
          certifications: cleanArray(fd.get('certifications')),
          summary: fd.get('summary') || null,
          recruiter_id: state.user.id
        };
        const { data, error } = await sb.from('candidates').insert(payload).select().single();
        if (error) throw error;

        const file = fd.get('resume');
        if (file && file.size) await uploadCandidateDocument(data.id, file);

        await syncCandidateTaxonomy(data.id, payload.skills, payload.certifications);

        await logActivity('candidate_added', `Candidate added: ${payload.first_name} ${payload.last_name}`, payload.current_title || '', {candidate_id:data.id});
      }

      if (type === 'client') {
        const payload = {
          organization_id: state.orgId,
          name: fd.get('name'),
          industry: fd.get('industry') || null,
          status: fd.get('status'),
          phone: fd.get('phone') || null,
          website: fd.get('website') || null,
          city: fd.get('city') || null,
          state: fd.get('state') || null,
          placement_fee_pct: cleanNumber(fd.get('placement_fee_pct')),
          contract_markup_pct: cleanNumber(fd.get('contract_markup_pct')),
          notes: fd.get('notes') || null,
          account_owner_id: state.user.id
        };
        const { data, error } = await sb.from('clients').insert(payload).select().single();
        if (error) throw error;
        await logActivity('client_added', `Client added: ${payload.name}`, payload.industry || '', {client_id:data.id});
      }


      if (type === 'contact') {
        const payload = {
          organization_id: state.orgId,
          client_id: fd.get('client_id'),
          first_name: fd.get('first_name'),
          last_name: fd.get('last_name'),
          title: fd.get('title') || null,
          email: fd.get('email') || null,
          phone: fd.get('phone') || null,
          is_decision_maker: fd.get('is_decision_maker') === 'true',
          notes: fd.get('notes') || null
        };
        const { data, error } = await sb.from('contacts').insert(payload).select().single();
        if (error) throw error;
        await logActivity('contact_added', `Contact added: ${payload.first_name} ${payload.last_name}`, payload.title || '', {client_id:payload.client_id,contact_id:data.id});
      }

      if (type === 'job') {
        const payload = {
          organization_id: state.orgId,
          client_id: fd.get('client_id'),
          title: fd.get('title'),
          city: fd.get('city') || null,
          state: fd.get('state') || null,
          employment_type: fd.get('employment_type'),
          openings: Number(fd.get('openings') || 1),
          pay_min: cleanNumber(fd.get('pay_min')),
          pay_max: cleanNumber(fd.get('pay_max')),
          bill_rate: cleanNumber(fd.get('bill_rate')),
          start_date: fd.get('start_date') || null,
          required_skills: cleanArray(fd.get('required_skills')),
          required_certifications: cleanArray(fd.get('required_certifications')),
          description: fd.get('description') || null,
          recruiter_id: state.user.id,
          status: 'open'
        };
        const { data, error } = await sb.from('jobs').insert(payload).select().single();
        if (error) throw error;
        await logActivity('job_added', `Job opened: ${payload.title}`, '', {job_id:data.id,client_id:payload.client_id});
      }

      if (type === 'submission') {
        const payload = {
          organization_id: state.orgId,
          candidate_id: fd.get('candidate_id'),
          job_id: fd.get('job_id'),
          recruiter_id: state.user.id,
          status: fd.get('status'),
          candidate_rate: cleanNumber(fd.get('candidate_rate')),
          client_bill_rate: cleanNumber(fd.get('client_bill_rate')),
          recruiter_notes: fd.get('recruiter_notes') || null,
          submitted_at: ['submitted','client_review','interview','offer','placed'].includes(fd.get('status')) ? new Date().toISOString() : null
        };
        const { data, error } = await sb.from('submissions').insert(payload).select().single();
        if (error) throw error;
        await logActivity('submission_added','Candidate linked to job',payload.status,{candidate_id:payload.candidate_id,job_id:payload.job_id});
      }


      if (type === 'interview') {
        const payload = {
          organization_id: state.orgId,
          submission_id: fd.get('submission_id'),
          interview_type: fd.get('interview_type'),
          starts_at: new Date(fd.get('starts_at')).toISOString(),
          location_or_link: fd.get('location_or_link') || null,
          interviewer_names: fd.get('interviewer_names') || null,
          status: fd.get('status')
        };
        const { data, error } = await sb.from('interviews').insert(payload).select().single();
        if (error) throw error;
        const sub = state.data.submissions.find(x=>x.id===payload.submission_id);
        await logActivity('interview_scheduled','Interview scheduled',payload.starts_at,{candidate_id:sub?.candidate_id,job_id:sub?.job_id});
      }

      if (type === 'offer') {
        const sub = state.data.submissions.find(x=>x.id===fd.get('submission_id'));
        if (!sub) throw new Error('Choose a valid submission.');
        const job = state.data.jobs.find(x=>x.id===sub.job_id);
        if (!job) throw new Error('Submission job could not be resolved.');
        const payload = {
          organization_id: state.orgId,
          submission_id: sub.id,
          candidate_id: sub.candidate_id,
          job_id: sub.job_id,
          client_id: job.client_id,
          offer_type: fd.get('offer_type'),
          status: fd.get('status'),
          salary: cleanNumber(fd.get('salary')),
          hourly_rate: cleanNumber(fd.get('hourly_rate')),
          bill_rate: cleanNumber(fd.get('bill_rate')),
          bonus: cleanNumber(fd.get('bonus')),
          start_date: fd.get('start_date') || null,
          notes: fd.get('notes') || null
        };
        const { error } = await sb.from('offers').insert(payload);
        if (error) throw error;
        if (['sent','negotiating','accepted'].includes(payload.status)) {
          await sb.from('submissions').update({status:'offer'}).eq('id',sub.id);
        }
        await logActivity('offer_created','Offer created',payload.status,{candidate_id:sub.candidate_id,job_id:sub.job_id,client_id:job.client_id});
      }

      if (type === 'placement') {
        const job = state.data.jobs.find(x=>x.id===fd.get('job_id'));
        if (!job) throw new Error('Choose a valid job.');
        const payload = {
          organization_id: state.orgId,
          candidate_id: fd.get('candidate_id'),
          job_id: fd.get('job_id'),
          client_id: job.client_id,
          placement_type: fd.get('placement_type'),
          status: fd.get('status'),
          start_date: fd.get('start_date'),
          end_date: fd.get('end_date') || null,
          pay_rate: cleanNumber(fd.get('pay_rate')),
          bill_rate: cleanNumber(fd.get('bill_rate')),
          annual_salary: cleanNumber(fd.get('annual_salary')),
          placement_fee_pct: cleanNumber(fd.get('placement_fee_pct')),
          estimated_revenue: cleanNumber(fd.get('estimated_revenue')),
          recruiter_id: state.user.id
        };
        const { data, error } = await sb.from('placements').insert(payload).select().single();
        if (error) throw error;
        await sb.from('candidates').update({status:'placed'}).eq('id',payload.candidate_id);
        await logActivity('placement_added','Placement created',`${payload.placement_type} starting ${payload.start_date}`,{candidate_id:payload.candidate_id,job_id:payload.job_id,client_id:payload.client_id});
      }

      if (type === 'task') {
        const payload = {
          organization_id: state.orgId,
          title: fd.get('title'),
          due_at: fd.get('due_at') ? new Date(fd.get('due_at')).toISOString() : null,
          priority: fd.get('priority'),
          description: fd.get('description') || null,
          assigned_to: state.user.id,
          status: 'open'
        };
        const { error } = await sb.from('tasks').insert(payload);
        if (error) throw error;
      }

      closeModal();
      showToast('Saved.');
      await refreshAll();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Could not save.', true);
      if (btn) { btn.disabled = false; btn.textContent = 'Save'; }
    }
  }


  async function syncCandidateTaxonomy(candidateId, skillNames=[], certNames=[]) {
    for (const name of skillNames) {
      const normalized = String(name).trim();
      if (!normalized) continue;
      let { data: skill } = await sb.from('skills').select('id').eq('organization_id',state.orgId).eq('normalized_name',normalized.toLowerCase()).maybeSingle();
      if (!skill) {
        const created = await sb.from('skills').insert({organization_id:state.orgId,name:normalized}).select('id').single();
        if (created.error) throw created.error;
        skill = created.data;
      }
      const linked = await sb.from('candidate_skills').upsert({organization_id:state.orgId,candidate_id:candidateId,skill_id:skill.id},{onConflict:'candidate_id,skill_id'});
      if (linked.error) throw linked.error;
    }
    for (const name of certNames) {
      const normalized = String(name).trim();
      if (!normalized) continue;
      let { data: cert } = await sb.from('certifications').select('id').eq('organization_id',state.orgId).eq('normalized_name',normalized.toLowerCase()).maybeSingle();
      if (!cert) {
        const created = await sb.from('certifications').insert({organization_id:state.orgId,name:normalized}).select('id').single();
        if (created.error) throw created.error;
        cert = created.data;
      }
      const linked = await sb.from('candidate_certifications').upsert({organization_id:state.orgId,candidate_id:candidateId,certification_id:cert.id},{onConflict:'candidate_id,certification_id,credential_number'});
      if (linked.error && !String(linked.error.message||'').includes('no unique')) throw linked.error;
    }
  }

  async function uploadCandidateDocument(candidateId,file) {
    const allowed=['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','image/jpeg','image/png'];
    if (file.size > 10 * 1024 * 1024) throw new Error('File must be 10 MB or smaller.');
    if (file.type && !allowed.includes(file.type)) throw new Error('Unsupported document type.');
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,'_');
    const path = `${state.orgId}/candidates/${candidateId}/${Date.now()}-${safeName}`;
    const { error: uploadError } = await sb.storage.from('staffing-documents').upload(path,file,{upsert:false,contentType:file.type || undefined});
    if (uploadError) throw uploadError;
    const { error: rowError } = await sb.from('candidate_documents').insert({
      organization_id: state.orgId,
      candidate_id: candidateId,
      document_type: 'resume',
      file_name: file.name,
      storage_path: path,
      mime_type: file.type || null
    });
    if (rowError) throw rowError;
  }

  async function completeTask(id) {
    const { error } = await sb.from('tasks').update({status:'completed',completed_at:new Date().toISOString()}).eq('id',id);
    if (error) return showToast(error.message,true);
    await logActivity('task_completed','Task completed','',{});
    await refreshAll();
  }

  sb.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      app.hidden = true;
      authScreen.hidden = false;
    }
  });

  boot();
}
